import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

/** Évènement non standard émis par Chromium (Chrome, Edge, Samsung Internet) quand l'application est installable. */
interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
	interface Window {
		/** Posé par index.html : l'évènement peut partir avant le démarrage d'Angular. */
		deferredPwaPrompt?: BeforeInstallPromptEvent;
	}
}

/**
 * native : le navigateur sait installer l'application (invite système après un clic sur « Installer »)
 * ios    : Safari n'a pas d'invite, l'utilisateur doit passer par « Sur l'écran d'accueil »
 */
export type PwaInstallMode = "native" | "ios";

/**
 * Posée quand l'utilisateur ferme le dialogue (« Plus tard »). Dans sessionStorage : elle ne vaut que pour la visite
 * en cours, le dialogue revient à la visite suivante tant que l'application n'est pas installée.
 */
const PWA_DIALOG_DISMISSED_KEY = "pwa_install_dialog_dismissed";

/** Lancée depuis l'écran d'accueil ou dans une fenêtre d'application : elle est installée sur cet appareil. */
export function isRunningAsInstalledApp(): boolean {
	const standalone = ["standalone", "fullscreen", "minimal-ui"].some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
	return standalone || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

@Injectable({ providedIn: "root" })
export class PwaInstallService {
	private deferredPrompt: BeforeInstallPromptEvent | null = null;
	/** Garde la fermeture même si sessionStorage est indisponible : le dialogue ne revient pas pendant ce chargement. */
	private dismissed = false;
	private readonly availableSubject = new BehaviorSubject<PwaInstallMode | null>(null);
	private readonly proposalSubject = new BehaviorSubject<PwaInstallMode | null>(null);
	private readonly reopenSubject = new Subject<PwaInstallMode>();

	/**
	 * Mode d'installation possible sur cet appareil, ou null (déjà installée, navigateur qui ne sait pas installer).
	 * Reste renseigné après un « Plus tard » : c'est ce qui permet d'afficher un bouton « Installer l'application ».
	 */
	readonly available$: Observable<PwaInstallMode | null> = this.availableSubject.asObservable();

	/**
	 * Proposition automatique : le mode à afficher d'office, null pour fermer. Une fois par visite, tant que
	 * l'application n'est pas installée : un « Plus tard » ne vaut que pour la visite en cours.
	 */
	readonly mode$: Observable<PwaInstallMode | null> = this.proposalSubject.asObservable();

	/** Demande d'affichage faite par l'utilisateur (bouton du menu). */
	readonly reopen$: Observable<PwaInstallMode> = this.reopenSubject.asObservable();

	constructor() {
		if (isRunningAsInstalledApp()) return;

		// L'évènement est écouté même après un « Plus tard » : sans lui, impossible de rouvrir l'invite depuis le menu.
		if (window.deferredPwaPrompt) {
			this.deferredPrompt = window.deferredPwaPrompt;
			this.offer("native");
		}

		window.addEventListener("beforeinstallprompt", (event) => {
			event.preventDefault();
			this.deferredPrompt = event as BeforeInstallPromptEvent;
			this.offer("native");
		});

		// installée par un autre chemin (menu du navigateur, icône de la barre d'adresse)
		window.addEventListener("appinstalled", () => this.markInstalled());

		if (this.isIos()) {
			this.offer("ios");
		}
	}

	/** Ouvre l'invite d'installation du navigateur. À appeler depuis un clic de l'utilisateur. */
	async install(): Promise<void> {
		const prompt = this.deferredPrompt;
		this.markInstalled();
		if (!prompt) return;

		await prompt.prompt();
		await prompt.userChoice;
	}

	/**
	 * Ferme la proposition pour la visite en cours (« Plus tard »). Elle reviendra à la prochaine visite si l'application
	 * n'est toujours pas installée. L'installation reste possible depuis le bouton du menu : l'évènement du navigateur
	 * est conservé.
	 */
	dismiss(): void {
		this.rememberDismissal();
		this.proposalSubject.next(null);
	}

	/** Rouvre le dialogue à la demande de l'utilisateur, même après un « Plus tard ». Sans effet si l'installation n'est pas possible. */
	reopen(): void {
		const mode = this.availableSubject.value;
		if (mode) this.reopenSubject.next(mode);
	}

	/** L'installation est possible ; on la propose d'office, sauf si l'utilisateur a déjà fermé le dialogue pendant cette visite. */
	private offer(mode: PwaInstallMode): void {
		this.availableSubject.next(mode);
		if (!this.isDismissedThisVisit()) this.proposalSubject.next(mode);
	}

	/** Installée (ou en cours) : plus rien à proposer. L'évènement du navigateur ne peut servir qu'une fois. */
	private markInstalled(): void {
		this.deferredPrompt = null;
		this.rememberDismissal();
		this.availableSubject.next(null);
		this.proposalSubject.next(null);
	}

	private isIos(): boolean {
		const isIphoneOrIpad = /iphone|ipad|ipod/i.test(navigator.userAgent);
		// iPadOS se présente comme un Mac, mais lui a un écran tactile
		const isIpadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
		return isIphoneOrIpad || isIpadOs;
	}

	// sessionStorage peut être indisponible (stockage bloqué) : on ne casse pas l'application pour ça
	private isDismissedThisVisit(): boolean {
		if (this.dismissed) return true;
		try {
			return sessionStorage.getItem(PWA_DIALOG_DISMISSED_KEY) !== null;
		} catch {
			return false;
		}
	}

	private rememberDismissal(): void {
		this.dismissed = true;
		try {
			sessionStorage.setItem(PWA_DIALOG_DISMISSED_KEY, "1");
		} catch {
			// rien : seul le rechargement de la page fera revenir le dialogue
		}
	}
}
